import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { TokenBucket } from "@/lib/token-bucket";
const RXTERMS_SEARCH_URL = "https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search";
const RXTERMS_TIMEOUT_MS = 2_000;
const RXTERMS_CACHE_TIME_MS = 24 * 60 * 60 * 1_000;
const MAX_RESULTS = 10;
const rxTermsRateLimiter = new TokenBucket(10, 10);
export const RXTERMS_MIN_SEARCH_LENGTH = 3;
export interface PatientReportedMedicationOption {
  value: string;
  strengthFormRxId: string;
}
export interface PatientReportedMedicationSuggestion {
  name: string;
  conceptRxId: string | null;
  options: PatientReportedMedicationOption[];
}
export function parseRxTermsSuggestions(data: unknown, maxResults = MAX_RESULTS): PatientReportedMedicationSuggestion[] {
  if (!Array.isArray(data) || data.length < 4 || !Array.isArray(data[3])) {
    return [];
  }
  const extraFields = data.find(isRecord) ?? {};
  const strengthsByResult = toNestedArray(extraFields.STRENGTHS_AND_FORMS);
  const strengthFormRxIdsByResult = toNestedArray(extraFields.RXCUIS);
  const conceptRxIds = Array.isArray(extraFields.SXDG_RXCUI) ? extraFields.SXDG_RXCUI : [];
  const suggestions: PatientReportedMedicationSuggestion[] = [];
  for (let index = 0; index < Math.min(data[3].length, maxResults); index++) {
    const name = extractName(data[3][index]);
    if (!name) continue;
    const strengths = strengthsByResult[index] ?? [];
    const strengthFormRxIds = strengthFormRxIdsByResult[index] ?? [];
    const options: PatientReportedMedicationOption[] = [];
    if (strengths.length === strengthFormRxIds.length) {
      for (let optionIndex = 0; optionIndex < strengths.length; optionIndex++) {
        const strength = normalizedString(strengths[optionIndex]);
        const strengthFormRxId = normalizedString(strengthFormRxIds[optionIndex]);
        if (strength && strengthFormRxId) {
          options.push({
            value: `${name} — ${strength}`,
            strengthFormRxId
          });
        }
      }
    }
    suggestions.push({
      name,
      conceptRxId: normalizedString(conceptRxIds[index]),
      options
    });
  }
  return suggestions;
}
export async function fetchPatientReportedMedicationSuggestions(query: string, signal?: AbortSignal): Promise<PatientReportedMedicationSuggestion[]> {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < RXTERMS_MIN_SEARCH_LENGTH) return [];
  await rxTermsRateLimiter.acquire(signal);
  const url = new URL(RXTERMS_SEARCH_URL);
  url.searchParams.set("terms", trimmedQuery);
  url.searchParams.set("maxList", String(MAX_RESULTS));
  url.searchParams.set("ef", "STRENGTHS_AND_FORMS,RXCUIS,SXDG_RXCUI");
  const controller = new AbortController();
  const abortRequest = () => controller.abort();
  const timeout = setTimeout(abortRequest, RXTERMS_TIMEOUT_MS);
  if (signal?.aborted) abortRequest();else signal?.addEventListener("abort", abortRequest, {
    once: true
  });
  try {
    const response = await fetch(url, {
      signal: controller.signal
    });
    if (!response.ok) {
      throw new Error(`RxTerms request failed with status ${response.status}`);
    }
    return parseRxTermsSuggestions(await response.json());
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abortRequest);
  }
}
export function usePatientReportedMedicationSuggestions(query) {
  const $ = _c(9);
  let t0;
  if ($[0] !== query) {
    t0 = query.trim();
    $[0] = query;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trimmedQuery = t0;
  let t1;
  let t2;
  if ($[2] !== trimmedQuery) {
    t1 = ["patient-reported-medication-suggestions", trimmedQuery];
    t2 = t3 => {
      const {
        signal
      } = t3;
      return fetchPatientReportedMedicationSuggestions(trimmedQuery, signal);
    };
    $[2] = trimmedQuery;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = trimmedQuery.length >= RXTERMS_MIN_SEARCH_LENGTH;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: RXTERMS_CACHE_TIME_MS,
      gcTime: RXTERMS_CACHE_TIME_MS,
      retry: false
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function toNestedArray(value: unknown): unknown[][] {
  if (!Array.isArray(value) || value.length === 0) return [];
  if (value.every(item => !Array.isArray(item))) return [value];
  return value.map(item => Array.isArray(item) ? item : []);
}
function extractName(value: unknown): string | null {
  if (typeof value === "string") return normalizedString(value);
  if (Array.isArray(value)) return normalizedString(value[0]);
  return null;
}
function normalizedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.trim() || null;
}